import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useContent } from '@/stores/RootStore';
import logoUrl from '@svg/evstech-logo-white.svg';
import s from './Footer.module.scss';

// [2026-10-11 사용자] 푸터 라벨 → 경로를 실제 NAV(mockData NAV.children)와 1:1 동기화. 전부 유효 경로(미연결/죽은 링크 제거).
//   근거: [[evs-footer-nav-consistency-todo]]. FOOTER_COLS(mockData)와 라벨·순서 일치.
const FOOT_LINKS: Record<string, string> = {
  // 제품
  '전체 제품': '/products', 'EVS-G200 게이트웨이': '/products/evs-g200', '환경 계측기 4종': '/products/instruments',
  'AI IoT 체감온도계': '/products/heat-index', '모니터·TV 연결 중계기': '/products/display-repeater',
  // 안전
  '안전 솔루션': '/safety', '체감온도 통합관제 플랫폼': '/safety/platform', '온열질환 예방': '/safety/heat',
  '한랭작업 관리': '/safety/cold', '밀폐공간 가스안전': '/safety/gas',
  // 환경
  '환경 IoT 솔루션': '/environment', 'IoT 게이트웨이 제조': '/business/igm', 'IoT 설치사업': '/business/install',
  '배출·방지시설 안내': '/guide', 'GreenLink 전송': '/environment/greenlink',
  // 설치안내
  '도입 절차': '/install/process', '현장실사·준비자료': '/install/site-survey', '설치 후 운영': '/install/operation',
  // 설치 사례
  '환경 설치사례': '/cases?tab=environment', '안전 설치사례': '/cases?tab=safety', 'A/S·개선사례': '/cases?tab=as', '주요 거래처·실적': '/cases/clients',
  // 고객지원
  '실시간 상담': '/support/consult', '문의하기': '/support/inquiry', '내 문의': '/support/my-inquiries',
  'A/S 신청·현황': '/support/as', 'FAQ': '/support/faq', '자료실': '/support/resources', '뉴스·소식': '/news',
  // 회사소개
  '인사말·회사개요': '/company', '연혁·조직도': '/company/history', '인증·기술·연구소': '/company/certifications', '오시는 길': '/company/location',
};

const Footer = observer(() => {
  const { company, footerCols } = useContent();
  const navigate = useNavigate();
  const go = (label: string) => (e: React.MouseEvent) => { e.preventDefault(); const to = FOOT_LINKS[label]; if (to) navigate(to); };
  return (
    <footer className={s.footer}>
      <div className={s.inner}>
        <div className={s.brandCol}>
          <img src={logoUrl} alt="EVS" className={s.logo} />
          <p className={s.addr}>본사 · {company.hqAddress}</p>
          <p className={s.addr}>R&amp;D센터 · {company.rndAddress}</p>
          <p className={s.addr}>대한민국 | Tel. {company.tel}</p>
          <div className={s.btns}>
            <a href="/company/location" className={s.ghost} onClick={go('오시는 길')}>오시는 길</a>
            <a href="/support/inquiry" className={s.primary} onClick={(e) => { e.preventDefault(); navigate('/support/inquiry'); }}>문의하기</a>
          </div>
          {/* 카톡·블로그 — evs.com/as 푸터 스타일(FOLLOW US ON + 흰 모노크롬 아이콘 한 줄).
              아이콘은 카톡·네이버 블로그로 재해석. 실제 채널 링크(evstech.co.kr), 새 탭. (2026-07-31) */}
          <div className={s.social}>
            <span className={s.followLabel}>FOLLOW US ON</span>
            <div className={s.snsRow}>
              <a href="http://pf.kakao.com/_xikTUn/chat" target="_blank" rel="noopener noreferrer"
                 className={s.sns} aria-label="카카오톡 채널" title="카카오톡 채널">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 4.4C7 4.4 3 7.6 3 11.5c0 2.45 1.63 4.6 4.1 5.86-.18.63-.65 2.28-.74 2.6-.11.42.15.42.33.3.14-.09 2.24-1.5 3.15-2.12.7.1 1.42.16 2.16.16 5 0 9-3.2 9-6.8S17 4.4 12 4.4z"/>
                </svg>
              </a>
              <a href="https://blog.naver.com/evstech" target="_blank" rel="noopener noreferrer"
                 className={s.sns} aria-label="네이버 블로그" title="네이버 블로그">
                {/* 네이버 블로그 앱 아이콘 느낌: 라운드 사각 배지 + 'b' 음각(evenodd). 카톡(채운 실루엣)과 통일. */}
                <svg width="25" height="25" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6.6 3h10.8A3.6 3.6 0 0 1 21 6.6v10.8a3.6 3.6 0 0 1-3.6 3.6H6.6A3.6 3.6 0 0 1 3 17.4V6.6A3.6 3.6 0 0 1 6.6 3Zm2.55 3.55a1.15 1.15 0 0 0-2.3 0v8.2a1.15 1.15 0 0 0 2.19.48 3.62 3.62 0 0 0 2.42.92 3.9 3.9 0 1 0 0-7.8 3.6 3.6 0 0 0-2.31.83V6.55Zm2.31 4.55a1.95 1.95 0 1 1 0 3.9 1.95 1.95 0 0 1 0-3.9Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        {footerCols.map((col) => (
          <div key={col.title} className={`${s.col} ${col.dim ? s.dim : ''}`}>
            <h4>{col.title}</h4>
            {col.links.map((l) => (
              <a key={l} href={FOOT_LINKS[l] || '#'} onClick={FOOT_LINKS[l] ? go(l) : (e) => e.preventDefault()}
                style={{ cursor: FOOT_LINKS[l] ? 'pointer' : 'default' }}>{l}</a>
            ))}
          </div>
        ))}
      </div>
      <div className={s.bottom}>
        <span>© {company.name} All rights reserved · 사업자번호 {company.bizNo}</span>
        <span>이용약관 · 개인정보처리방침</span>
      </div>
    </footer>
  );
});

export default Footer;
